<template>
  <div class="row p-0">
    <div class="row flex-row-reverse balance-summary m-0 p-0">
      <b-table-simple class="export-table balance-summary" outlined borderless style="max-width: 300px">
        <b-thead>
          <b-tr class="title-row">
            <b-th colspan="2">{{ lodash.startCase($t('sale_notes.resumes.total')) }}</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr>
            <b-th class="text-bold">{{ lodash.startCase($t('sale_notes.resumes.purchased_shrimp')) }}</b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(totalShrimp) }}</b-th>
          </b-tr>
          <b-tr>
            <b-th class="text-bold">{{ lodash.startCase($t('unloads.singular')) }}</b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(totalUnload) }}</b-th>
          </b-tr>
          <b-tr>
            <b-th class="text-bold">{{ lodash.startCase($t('sale_notes.resumes.PPh')) }}</b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(pph) }}</b-th>
          </b-tr>
          <b-tr v-if="activeTab === 'admin'">
            <b-th class="text-bold">{{ lodash.startCase($t('sale_notes.resumes.acceleration_fee')) }}</b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(accelerationFee) }}</b-th>
          </b-tr>
          <b-tr v-if="activeTab === 'admin'">
            <b-th class="text-bold">
              {{ lodash.startCase($t('sale_notes.shows.unloading_cost_by_jala')) }}
              </b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(totalExpenseByJala) }}</b-th>
          </b-tr>
          <b-tr>
            <b-th class="text-bold">
              {{ lodash.startCase($t('sale_notes.shows.unloading_cost_by_partner')) }}
            </b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(totalExpenseByPartner) }}</b-th>
          </b-tr>
          <b-tr>
            <b-th class="text-bold">{{ lodash.startCase($t('sale_notes.resumes.JALA_margin')) }}</b-th>
            <b-th class="text-bold" style="text-align: right">{{ currencyFormatter(jalaMargin) }}</b-th>
          </b-tr>
          <b-tr v-if="partnerMargin">
            <b-th :class="{'text-success': partnerMargin > 0, 'text-danger': partnerMargin < 0}">{{ lodash.upperCase($t('sale_notes.resumes.transfer_to_partner')) }}</b-th>
            <b-th :class="{'text-success': partnerMargin > 0, 'text-danger': partnerMargin < 0}" style="text-align: right">{{ currencyFormatter(partnerMargin) }}</b-th>
          </b-tr>
          <b-tr v-if="partnerDebt">
            <b-th class="text-danger">{{ lodash.upperCase($t('sale_notes.resumes.partner_debt')) }}</b-th>
            <b-th class="text-danger" style="text-align: right">{{ currencyFormatter(partnerDebt) }}</b-th>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>
  </div>
</template>

<script>
export default {
  name: 'summary-card-export',
  props: {
    value: { type: Object, default: function () { return {} }},
    activeTab: {type: String, default: 'admin'}
  },
  data() {
    return {
      model: this.value,
      partnerMarginVisible: true,
    }
  },
  computed: {
    harvestShrimpTotal() {
      return Math.abs(this.model?.sales_reports?.filter(({category, sub_category}) => category === 'cost_of_goods_sold' && sub_category === 'harvest').reduce((carry, { total }) => carry + (total ?? 0), 0))
    },
    isKmService () {
      return this.model?.trip?.type === 'deposit_goods'
    },
    iceCost() {
      return -this.value?.sales_reports?.
          filter(({ category, sub_category }) => category.toLowerCase() === 'cost_of_goods_sold' && sub_category === 'ice').
          reduce((carry, { total }) => carry + total, 0) ?? 0
    },
    deliveryCost() {
      return -this.value?.sales_reports?.
          filter(({ category, sub_category }) => category.toLowerCase() === 'expense' && sub_category === 'delivery').
          reduce((carry, { total }) => carry + total, 0) ?? 0
    },
    labourCost() {
      return -this.value?.sales_reports?.
          filter(({ category, sub_category }) => category.toLowerCase() === 'expense' && sub_category === 'labour').
          reduce((carry, { total }) => carry + total, 0) ?? 0
    },
    profitShare() {
      return this.value.profit_share?.total ?? 0
    },
    partnerDebt() {
      if (this.value.profit_share.total >= 0) return 0
      return -this.value.profit_share.total
    },
    partnerMargin() {
      return this.value.balancing_transaction?.total_debt ?? 0
    },
    jalaMargin() {
      return this.value?.sale_items?.reduce((carry, { margin }) => carry + margin, 0) ?? 0
    },
    accelerationFee() {
      return -this.value?.sale_additional_items?.filter(({ name }) => name.toLowerCase() === 'biaya percepatan').reduce((carry, { total }) => carry + total, 0) ?? 0
    },
    pph() {
      return -this.value?.sale_additional_items?.filter(({ name }) => name.toLowerCase() === 'pph 22').reduce((carry, { total }) => carry + total, 0) ?? 0
    },
    saleItems() {
      return this.value?.sale_items ?? []
    },
    balanceTableItems() {
      return this.model.sale_items.map(({
        quality,
        quantity,
        size,
        unit_price,
        total_price,
      }) => ({
        product_name: `vannamei/${this.qualityFormatter(quality)}/Size ${size}`,
        quantity: quantity,
        price: unit_price,
        total_price: total_price,
      }))
    },
    totalShrimp() {
      return this.model?.sale_item_total_initial ?? 0
    },
    totalUnload() {
      return this.value?.sale_items?.reduce((carry, { total_price }) => carry + total_price, 0) ?? 0
    },
    totalBiomass() {
      return this.saleItems.reduce((carry, { quantity }) => carry + quantity, 0) ?? 0
    },
    totalPrice() {
      return this.saleItems.reduce((carry, { total_price }) => carry + total_price, 0) ?? 0
    },
    totalExpenseByJala() {
      return -this.value?.sales_reports?.
          filter(({ category, sub_category }) => category.toLowerCase() === 'expense' && sub_category.toLowerCase() === 'paid_by_jala').
          reduce((carry, { total }) => carry + total, 0)
    },
    totalExpenseByPartner() {
      return -this.value?.sales_reports?.
          filter(({ category, sub_category }) => category === 'expense' && sub_category === 'paid_by_partner').
          reduce((carry, { total }) => carry + total, 0)
    },
  },
  methods: {
    currencyFormatter(value) {
      return this.$numeral(value).format('$ 0,0.[00]')
    },
    decimalFormatter(value) {
      return this.$numeral(value).format('0,0.[00]')
    },
    qualityFormatter(quality) {
      return ['fresh', 'merah'].includes(quality) ? this.lodash.upperFirst(quality) : this.lodash.upperCase(quality)
    }
  }
}
</script>

<style lang="scss" scoped>
.text-bold {
  color: #161B4D !important;
  font-weight: 700;
}
table.balance-summary {
  thead {
    tr:nth-child(2) {
      td, th {
        padding-top: 8px !important;
      }
    }
    tr:last-child {
      td, th {
        padding-bottom: 8px !important;
      }
    }
    tr.title-row {
      color: white !important;
      th, td {
        padding: 8px 16px;
        border-color: #0084F3;
      }
    }
    tr {
      td, th {
        color: #161B4D;
      }
    }
    tr.child {
      td, th {
        color: #ACAEBD;
      }
    }
    td, th {
      padding: 4px 16px;
      background-color: #F9FAFB;
    }
  }
}
</style>